export type CardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'discover'
  | 'diners'
  | 'jcb'
  | 'unionpay'
  | 'unknown';

export enum PricingModelType {
  NON_CATEGORIZED = 'NON_CATEGORIZED',
  STANDARD = 'STANDARD',
  EVENT = 'EVENT',
  SUBSCRIPTION = 'SUBSCRIPTION',
}

export interface Pricing {
  id: string;
  price: number;
  unit: string;
  unitQuantity: number;
  preAuthAmount: number;
  unreturnedFee?: number;
  currency: string;
  symbol?: string;
  /**
   * Indicates whether this pricing supports battery swaps for subscriptions.
   */
  allowBatterySwap: boolean;
  /**
   * Maximum number of swaps allowed under this pricing.
   */
  maxSwap: number;
  maxRentTime?: number;
  maxRentTimeUnit?: string;
  /**
   * Maximum amount that can be charged for this pricing. Campus card rentals
   * require the card balance to cover this amount.
   */
  maxChargeAmount: number;
  enableFullPreAuthForDebit?: boolean;
  enableFullPreAuthForPrepaid?: boolean;
  /**
   * Fee charged to unlock the charger, on top of `price`.
   */
  unlockFee?: number;
  /**
   * `true` for a recurring pass (a subscription), `false` for a one-off pass.
   * Drives the wording of `displayModel.title` — "Monthly Subscription" vs
   * "30-Day Pass" — and whether cancel/resubscribe applies at all.
   */
  autoRenew?: boolean;
  /**
   * Ships on pricings but nothing reads it yet — trials, intro pricing and
   * event promotions are V1, not MVP.
   */
  acquisitionOfferType?: string;
  pricingModelType?: PricingModelType;
  displayMessage?: string;
}

export enum PricingRateType {
  FLAT = 'FLAT',
  TIME_BASED = 'TIME_BASED',
}
export interface PricingDisplayModel {
  /**
   * Pricing Name
   */
  name: string;

  /**
   * Pricing Title
   */
  title: string;

  /**
   * Pricing Rate Type
   *
   * Return `FLAT` or `TIME_BASED` based on the pricing model.
   */
  rateType: PricingRateType;

  /**
   * The instruction display message for unlock fee.
   */
  unlockFeeInstruction: string | null;

  /**
   * The instruction display message for free time.
   */
  freeTimeInstruction: string | null;

  /**
   * The instruction display message for pricing.
   */
  pricingInstruction: string;

  /**
   * The instruction display message for maximum fee version 2
   *
   * Return `flat rate` or `Max $xx` based on the pricing model.
   */
  maxFeeInstructionV2: string | null;

  /**
   * The instruction display message for the return fee version 2 ("Return charger to avoid lost fees.")
   */
  returnInstructionV2: string | null;

  /**
   * The instruction display message for pre-authorization amount version 2 ("Temporary hold may apply at start of rental.")
   */
  preAuthInstructionV2: string | null;

  /**
   * Indicates the rent time instruction.
   * For batter swap, display "Keep for X hours". For normal rental, display "Pay only for the time you use"
   */
  rentTimeInstruction: string | null;

  /**
   * The number of battery user can take
   * For batter swap, display "X charger swaps". For normal rental, display "1 charger"
   */
  rentChargerInstruction: string | null;
}

export interface StationPricingModel {
  displayModel: PricingDisplayModel;
  model: Pricing;
}

export interface StationPricings {
  currency: string;
  symbol: string;
  allowSubscription: boolean;
  firstPricing: StationPricingModel;
  mainPricing: StationPricingModel;
  secondPricing: StationPricingModel | null;
  memberBenefitsCampaignEnabled: boolean;
  memberBenefitsCampaignPricing: StationPricingModel | null;
  /**
   * The passes whose enabled pricing scopes admit this station, in the
   * station's currency.
   *
   * Already excludes anything returned as `mainPricing` / `secondPricing`, so
   * it renders alongside those with no de-duplication needed here. Empty
   * whenever `allowSubscription` is false.
   */
  subscriptionPricings?: StationPricingModel[];
}
